import React, { Fragment, createElement } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

const DEFAULTS = {
  description: 'Your favorite children’s luxury brands and independent boutiques, all in one magical place. Shop Maisonette for the best in kids clothing, furniture, decor, or toys.',
  image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette.jpg`,
  title: 'Maisonette - Online Shop For Children\'s Clothes, Furniture, Decor, Gifts - Maisonette'
};

export const getMetaImage = (props) => {
  if (props?.page?.socialImage?.uri) {
    const img = props.page.socialImage;

    if (img.types?.length > 0) {
      const type = img.types[img.types.length - 1];

      let src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName.split('.')[0]}-large.${type}`;
      if (props?.preview) {
        src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName}`;
      } else if (img.raw || !img.uri) {
        src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName}`;
      }

      return src;
    }
  }

  return DEFAULTS.image;
};

export const meta = {
  title: DEFAULTS.title,
  description: DEFAULTS.description,
  og: {
    description: DEFAULTS.description,
    image: DEFAULTS.image,
    title: DEFAULTS.title,
    type: 'website',
    url: process.env.NEXT_PUBLIC_CLIENT_HOST
  },
  twitter: {
    card: 'summary_large_image',
    description: DEFAULTS.description,
    image: DEFAULTS.image,
    title: DEFAULTS.title
  }
};

export const generateMeta = (o = {}) => {
  const options = { ...meta, key: 'default', ...o };
  const children = [];

  /* HTML Meta Tags */
  children.push(createElement('title', { key: 'title' }, options.title));
  children.push(createElement('meta', { key: 'description', name: 'description', content: options.description }));

  /* Facebook Meta Tags */
  Object.entries(options.og).forEach(([key, value]) => {
    if (value != null) children.push(createElement('meta', { key: `og-${key}`, property: `og:${key}`, content: value }));
    else children.push(createElement('meta', { key: `og-${key}`, property: `og:${key}`, content: meta.og[key] }));
  });

  /* Twitter Meta Tags */
  Object.entries(options.twitter).forEach(([key, value]) => {
    if (value != null) children.push(createElement('meta', { key: `twitter-${key}`, name: `twitter:${key}`, content: value }));
    else children.push(createElement('meta', { key: `twitter-${key}`, name: `twitter:${key}`, content: meta.twitter[key] }));
  });

  return createElement(Fragment, {}, [...children]);
};

export const getDescription = (key, values, mutator) => {
  const MAX_LENGTH = 158;
  const BreakException = {};

  if (values) {
    try {
      values.forEach((c, i) => {
        if (i === 0) mutator.push(`including ${c[key]}`);
        else if (i === values.length - 1) mutator.push(`and ${c[key]}`);
        else {
          const temp = mutator.slice(0);
          temp.push(c[key]);
          temp.push(` and ${values[i + 1][key]}`);

          if (`${temp.join(', ')}.`.length >= MAX_LENGTH) {
            mutator.push(`and ${c[key]}`);
            throw BreakException;
          } else {
            mutator.push(c[key]);
          }
        }
      });
    } catch (e) {
      if (e !== BreakException) throw e;
    }
  }

  return `${mutator.join(', ')}.`;
};

export const getPLPDescription = (activeCategories = [], facets = false, products = false) => {
  const description = [activeCategories.map((c) => c.name).join(' ')];

  if (!facets || !products) return DEFAULTS.description;

  if (activeCategories.length <= 0 || activeCategories.length === 3) {
    return getDescription('title', products, description);
  }

  const categories = facets.filter(
    (f) => /^cat[1-9]+$/.test(f.id) && +f.id.split('cat')[1] > activeCategories.length
  ).reduce((acc, curr) => [...acc, curr.values], [])[0];

  return getDescription('name', categories, description);
};

const getLPCMetaTitle = (lp) => (lp?.title ? lp?.title.split('<title>')[1].split('</title>')[0] : '');

const PLPMeta = (props) => {
  const getMetaData = () => {
    if (props.noResults) {
      if (props.searchTerm !== '*') {
        const title = `No results for '${props.searchTerm}'${props.correctedSearchTerm ? ` corrected from '${props.correctedSearchTerm}'` : ''} - Maisonette`;

        return generateMeta({
          key: 'no-results',
          title,
          facebook: { title },
          twitter: { title }
        });
      }

      const title = props.categories?.active
        ? `No results for ${props.categories?.active?.cat1?.name} - Maisonette`
        : 'No results - Maisonette';

      return generateMeta({
        key: 'no-results',
        title,
        facebook: { title },
        twitter: { title }
      });
    }

    if (props.searchTerm !== '*') {
      return generateMeta({
        key: 'search-results',
        title: `Search results for ${props.searchTerm} - Maisonette`,
        facebook: { title: `Search results for ${props.searchTerm} - Maisonette` },
        twitter: { title: `Search results for ${props.searchTerm} - Maisonette` }
      });
    }

    if (props.lpc) {
      return generateMeta({
        key: 'lpc',
        title: `${getLPCMetaTitle(props.lp)} - Maisonette`,
        description: getPLPDescription(
          Object.values(props.categories?.active ?? []), props?.facets, props?.results
        ),
        og: {
          title: `${getLPCMetaTitle(props.lp)} - Maisonette`,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        },
        twitter: {
          title: `${getLPCMetaTitle(props.lp)} - Maisonette`,
          image: props.results?.[0]?.image,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        }
      });
    }

    if (props.brand) {
      return generateMeta({
        key: 'brands',
        title: `${props.brand.name} - Maisonette`,
        description: `${props.brand.name} - ${props.brand.description ?? `includes ${props.results?.slice(0, 5).map((product) => product.title)}`}`,
        og: {
          description: `${props.brand.name} - ${props.brand.description ?? `includes ${props.results?.slice(0, 5).map((product) => product.title)}`}`,
          image: props.brand.icon !== `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default_taxon.jpg` ? props.brand.icon : props.results?.[0]?.image,
          title: `${props.brand.name} - Maisonette`,
          url: global.window?.location
        },
        twitter: {
          description: `${props.brand.name} - ${props.brand.description ?? `includes ${props.results?.slice(0, 5).map((product) => product.title)}`}`,
          image: props.brand.icon !== `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default_taxon.jpg` ? props.brand.icon : props.results?.[0]?.image,
          title: `${props.brand.name} - Maisonette`
        }
      });
    }

    if (props.edit) {
      return generateMeta({
        key: 'edit',
        title: `${props.editName} - Maisonette`,
        description: getPLPDescription(
          Object.values(props.categories?.active ?? []), props?.facets, props?.results
        ),
        og: {
          title: `${props.editName} - Maisonette`,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        },
        twitter: {
          title: `${props.editName} - Maisonette`,
          image: props.results?.[0]?.image,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        }
      });
    }

    if (props.trend) {
      const headers = props.headers
        ? ` - ${props.headers?.header?.name}${props.headers?.subHeaders?.map((h) => `, ${h?.name}`).join('') ?? ''}`
        : '';

      return generateMeta({
        key: 'trend',
        title: `Shop ${props.trendName}${headers} - Maisonette`,
        description: getPLPDescription(
          Object.values(props.categories?.active ?? []), props?.facets, props?.results
        ),
        og: {
          title: `Shop ${props.trendName}${headers} - Maisonette`,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        },
        twitter: {
          title: `Shop ${props.trendName}${headers} - Maisonette`,
          image: props.results?.[0]?.image,
          description: getPLPDescription(
            Object.values(props.categories?.active ?? []), props?.facets, props?.results
          )
        }
      });
    }

    return generateMeta({
      key: 'shop',
      title: `Shop ${props.headers?.header?.name ?? ''}${props.headers?.subHeaders?.map((h) => `, ${h?.name}`).join('') ?? ''} - Maisonette`,
      description: getPLPDescription(
        Object.values(props.categories?.active ?? []), props?.facets, props?.results
      ),
      og: {
        title: `Shop ${props.headers?.header?.name ?? ''}${props.headers?.subHeaders?.map((h) => `, ${h?.name}`).join('') ?? ''} - Maisonette`,
        image: props.results?.[0]?.image,
        url: global.window?.location,
        description: getPLPDescription(
          Object.values(props.categories?.active ?? []), props?.facets, props?.results
        )
      },
      twitter: {
        title: `Shop ${props.headers?.header?.name ?? ''}${props.headers?.subHeaders?.map((h) => `, ${h?.name}`).join('') ?? ''} - Maisonette`,
        image: props.results?.[0]?.image,
        description: getPLPDescription(
          Object.values(props.categories?.active ?? []), props?.facets, props?.results
        )
      }
    });
  };

  return <Head>{getMetaData(props)}</Head>;
};

PLPMeta.defaultProps = {
  brand: null,
  categories: null,
  correctedSearchTerm: null,
  edit: null,
  editName: null,
  headers: null,
  lp: null,
  lpc: null,
  noResults: false,
  results: null,
  searchTerm: '*',
  trend: null,
  trendName: null
};

PLPMeta.propTypes = {
  brand: PropTypes.object,
  categories: PropTypes.object,
  correctedSearchTerm: PropTypes.string,
  edit: PropTypes.string,
  editName: PropTypes.string,
  headers: PropTypes.object,
  lp: PropTypes.object,
  lpc: PropTypes.string,
  noResults: PropTypes.bool,
  results: PropTypes.array,
  searchTerm: PropTypes.string,
  trend: PropTypes.string,
  trendName: PropTypes.string
};

export default PLPMeta;
